import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./ui/table";
import { Button } from "./ui/button";
import { Edit, Trash2, ChevronDown, Calendar } from "lucide-react";
import moment from "moment";
import EmployeeDetailsModal from "./EmployeeDetailsModal";
import {
  isDateTimeInBusinessRange,
  formatCstDisplayDateTime
} from "../utils/cstDate";
import { getDynamicTaxRateLabel } from "../utils/dynamicTaxCalculator";
import { taxManagementService } from "../apis/taxManagementService";
import { positionSupportsOweToToast } from "../utils/employeeRoleUtils";
import { canPerformTransactionActions } from "../constants/sidebarTabs";

export const PositionTipsTransactionTable = ({
  records = [],
  currentPage = 1,
  setCurrentPage,
  entriesPerPage = 10,
  setEntriesPerPage,
  onEdit,
  onDelete,
  currentUser,
  dateRange,
  employeeApiService,
  employees = [],
  positionName = "Tip"
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "descending"
  });
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
  const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [modalRecipient, setModalRecipient] = useState(null);
  const [taxSettings, setTaxSettings] = useState([]);

  useEffect(() => {
    taxManagementService.getTaxSettings().then((res) => {
      if (res.success && Array.isArray(res.data)) {
        setTaxSettings(res.data);
      }
    });
  }, []);

  // Filter records by date range
  const filteredRecords = useMemo(() => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return records;
    }
    return records.filter((record) =>
      isDateTimeInBusinessRange(record.date, dateRange.start, dateRange.end)
    );
  }, [records, dateRange]);

  const isOther = String(positionName || "").trim().toLowerCase().includes("other");

  const getEmployeePosition = useCallback((record) => {
    if (record.position && record.position.toLowerCase() !== "other") {
      return record.position;
    }
    if (record.role && record.role.toLowerCase() !== "other") {
      return record.role;
    }
    const emp = employees.find((e) => e.name === record.employee);
    if (emp && emp.role) {
      return emp.role;
    }
    return record.position || record.role || "-";
  }, [employees]);

  // Sort records
  const sortedRecords = useMemo(() => {
    const sortableRecords = [...filteredRecords];

    if (sortConfig.key) {
      sortableRecords.sort((a, b) => {
        let aValue = a[sortConfig.key] || "";
        let bValue = b[sortConfig.key] || "";

        if (sortConfig.key === "position") {
          aValue = getEmployeePosition(a);
          bValue = getEmployeePosition(b);
        }

        if (sortConfig.key === "date") {
          aValue = moment(aValue).valueOf();
          bValue = moment(bValue).valueOf();
        }

        if (["tip_out_cash", "tax", "owe_to_toast"].includes(sortConfig.key)) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (sortConfig.key === "paid_out") {
          aValue = (parseFloat(a.tip_out_cash) || 0) - (parseFloat(a.tax) || 0);
          bValue = (parseFloat(b.tip_out_cash) || 0) - (parseFloat(b.tax) || 0);
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableRecords;
  }, [filteredRecords, sortConfig, getEmployeePosition]);

  // Pagination calculation
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = sortedRecords.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(sortedRecords.length / entriesPerPage) || 1;

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === "ascending" ? (
        <ChevronDown className="inline-block ml-1 h-4 w-4 rotate-180" />
      ) : (
        <ChevronDown className="inline-block ml-1 h-4 w-4" />
      );
    }
    return <ChevronDown className="inline-block ml-1 h-4 w-4 opacity-30" />;
  };

  const formatNumberWithCommas = (amount) => {
    const numericAmount = parseFloat(amount || 0);
    return numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleRecipientNameClick = async (recipientName, e) => {
    if (e) e.stopPropagation();

    const employee = employees.find((emp) => emp.name === recipientName);

    if (employee && employeeApiService) {
      setLoadingEmployeeDetails(true);
      setEmployeeDetailsError(null);
      setShowEmployeeDetailsModal(true);

      try {
        const startDate = dateRange?.start
          ? moment(dateRange.start).format("YYYY-MM-DD")
          : moment().subtract(30, "days").format("YYYY-MM-DD");
        const endDate = dateRange?.end
          ? moment(dateRange.end).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");

        const [completeDataResponse, performanceResponse] = await Promise.all([
          employeeApiService.getCompleteEmployeeData(employee.id, {
            startDate,
            endDate
          }),
          employeeApiService.getEmployeePerformanceMetrics(employee.id, {
            startDate,
            endDate
          })
        ]);

        if (completeDataResponse.success && performanceResponse.success) {
          setEmployeeDetails({
            employee,
            completeData: completeDataResponse?.data?.body?.data,
            performanceMetrics: performanceResponse?.data?.body?.data
          });
        } else {
          setEmployeeDetailsError("Failed to load employee details");
        }
      } catch (error) {
        console.error("Error loading employee details:", error);
        setEmployeeDetailsError("Error loading employee details");
      } finally {
        setLoadingEmployeeDetails(false);
      }
    } else {
      setModalRecipient(recipientName);
      setShowEmployeeDetailsModal(true);
    }
  };

  const handleEmployeeNameClick = (name, e) => {
    if (e) e.stopPropagation();
    handleRecipientNameClick(name, e);
  };

  const closeEmployeeDetailsModal = () => {
    setShowEmployeeDetailsModal(false);
    setEmployeeDetails(null);
    setEmployeeDetailsError(null);
  };

  const canEditDelete = canPerformTransactionActions(currentUser, positionName);

  const showOweToToast = positionSupportsOweToToast(positionName);

  const totalColumns = (canEditDelete ? 7 : 6) + (showOweToToast ? 1 : 0) + (isOther ? 1 : 0);

  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="icon"
          className="h-8 w-8"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return buttons;
  };

  return (
    <>
      <div className="bg-white rounded-md shadow overflow-hidden">
        {dateRange?.start && dateRange?.end && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
            <p className="text-sm text-blue-800">
              <Calendar className="inline h-4 w-4 mr-1" />
              Showing {positionName.toLowerCase()} tips from{" "}
              {moment(dateRange.start).format("MM/DD/YYYY")} to{" "}
              {moment(dateRange.end).format("MM/DD/YYYY")}
              <span className="ml-2 text-blue-600">
                ({filteredRecords.length} total records)
              </span>
            </p>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => requestSort("date")}
                >
                  Date {renderSortIcon("date")}
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => requestSort("employee")}
                >
                  Employee {renderSortIcon("employee")}
                </TableHead>
                {isOther && (
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => requestSort("position")}
                  >
                    Position {renderSortIcon("position")}
                  </TableHead>
                )}
                <TableHead
                  className="cursor-pointer"
                  onClick={() => requestSort("tip_out_cash")}
                >
                  Tips Received {renderSortIcon("tip_out_cash")}
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => requestSort("tax")}
                >
                  {getDynamicTaxRateLabel(positionName, taxSettings)} {renderSortIcon("tax")}
                </TableHead>
                <TableHead>
                  Paid Out
                </TableHead>
                {showOweToToast && (
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => requestSort("owe_to_toast")}
                  >
                    Owe To Toast {renderSortIcon("owe_to_toast")}
                  </TableHead>
                )}
                <TableHead
                  className="cursor-pointer"
                  onClick={() => requestSort("added_by")}
                >
                  Added By {renderSortIcon("added_by")}
                </TableHead>
                {canEditDelete && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentEntries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={totalColumns}
                    className="text-center py-8 text-gray-500"
                  >
                    {filteredRecords.length === 0
                      ? "No records found for the selected date range"
                      : "No records found"}
                  </TableCell>
                </TableRow>
              ) : (
                currentEntries.map((record) => {
                  const tipCash = parseFloat(record.tip_out_cash || 0);
                  const tax = parseFloat(record.tax || 0);
                  const paidOut = tipCash - tax;

                  return (
                    <TableRow key={`record-${record.id}`} className="hover:bg-gray-50">
                      <TableCell className="whitespace-nowrap">
                        {formatCstDisplayDateTime(record.date)}
                      </TableCell>
                      <TableCell className="font-medium text-blue-700">
                        <button
                          className="font-medium text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                          onClick={(e) =>
                            handleRecipientNameClick(record.employee, e)
                          }
                        >
                          {record.employee || "-"}
                        </button>
                      </TableCell>
                      {isOther && (
                        <TableCell className="text-gray-700 font-medium">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-200">
                            {getEmployeePosition(record)}
                          </span>
                        </TableCell>
                      )}
                      <TableCell className="font-semibold">
                        ${formatNumberWithCommas(record.tip_out_cash)}
                      </TableCell>
                      <TableCell className="text-red-500 font-semibold">
                        -${formatNumberWithCommas(record.tax)}
                      </TableCell>
                      <TableCell className="text-green-500 font-semibold">
                        ${formatNumberWithCommas(paidOut)}
                      </TableCell>
                      {showOweToToast && (
                        <TableCell className="text-purple-500 font-semibold">
                          ${formatNumberWithCommas(record.owe_to_toast)}
                        </TableCell>
                      )}
                      <TableCell>
                        <button
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                          onClick={(e) =>
                            handleEmployeeNameClick(
                              record.added_by || "Unknown",
                              e
                            )
                          }
                        >
                          {record.added_by || "-"}
                        </button>
                      </TableCell>
                      {canEditDelete && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(record);
                              }}
                              title="Edit Record"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(record);
                              }}
                              title="Delete Record"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex items-center">
            <span className="text-sm text-gray-700 mr-2">Showing</span>
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700 ml-2">
              of {sortedRecords.length} entries
            </span>
          </div>

          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            {renderPaginationButtons()}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <EmployeeDetailsModal
        isOpen={showEmployeeDetailsModal}
        onClose={closeEmployeeDetailsModal}
        loading={loadingEmployeeDetails}
        error={employeeDetailsError}
        employeeDetails={employeeDetails}
        dateRange={dateRange}
        fallbackName={modalRecipient}
      />
    </>
  );
};

export default PositionTipsTransactionTable;
